import type { GetUsersByUserIdResponse } from "@birdwatch/client";
import { useGetUsersByUserId, usePrefetch } from "@birdwatch/client";
import type { UseQueryOptions } from "@tanstack/react-query";

export type UseUserDetailOptions = Omit<
  UseQueryOptions,
  "queryKey" | "queryFn" | "select"
> & {
  /**
   * Comma-separated `?includes=` relations (e.g. `"categories,laborRate"`).
   * Requesting relations puts the result on its own cache entry, so leave it
   * unset unless the screen actually reads them.
   */
  includes?: string;
};

export const useUserDetail = (
  userId: string,
  options?: UseUserDetailOptions,
) => {
  const { includes, ...queryOptions } = options ?? {};
  const builder = useGetUsersByUserId({ userId });

  return (includes ? builder.query({ includes }) : builder).get({
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    ...(queryOptions as any),
  });
};

export const usePrefetchUserDetail = () => {
  const prefetch = usePrefetch();
  return async (userId: string) => {
    await prefetch(useGetUsersByUserId({ userId }));
  };
};

export type { GetUsersByUserIdResponse as UserDetailType };
